import { useState, useCallback } from 'react'
import { reserveSeats, releaseSeat } from '@services/seatMap.service'
import type { SeatData, SelectedSeat } from '@components/SeatMap/SeatMap.types'

interface UseSeatReservationResult {
  /** Seats the current session has reserved (shown as SELECTED). */
  selectedSeats: SelectedSeat[]
  /**
   * Server-generated token for this session.
   * MUST stay in React state — never written to localStorage/URL.
   */
  sessionToken: string | null
  /** Earliest expiry among selected seats (ISO datetime), or null. */
  reservedUntil: string | null
  /**
   * Seconds remaining when the FIRST seat was reserved.
   * Computed once at reservation time so the timer has a stable initial value.
   * Null if no seats are selected.
   */
  initialDurationSeconds: number | null
  /** Toggle: selects an AVAILABLE seat or deselects a SELECTED one. */
  toggleSeat: (
    eventId: number,
    seat: SeatData,
    onConflict: (seatId: number) => void,
  ) => Promise<void>
  /** Clear all selection state (called on expiry or navigation away). */
  clearSelection: () => void
}

/**
 * Manages seat reservation state for one checkout session.
 * The sessionToken is received from the server and lives only in React state.
 */
export function useSeatReservation(): UseSeatReservationResult {
  const [selectedSeats, setSelectedSeats] = useState<SelectedSeat[]>([])
  const [sessionToken, setSessionToken] = useState<string | null>(null)
  const [reservedUntil, setReservedUntil] = useState<string | null>(null)
  const [initialDurationSeconds, setInitialDurationSeconds] = useState<number | null>(null)

  const toggleSeat = useCallback(
    async (
      eventId: number,
      seat: SeatData,
      onConflict: (seatId: number) => void,
    ) => {
      const isAlreadySelected = selectedSeats.some((s) => s.seatId === seat.id)

      if (isAlreadySelected) {
        // Deselect: call release endpoint then update local state
        if (sessionToken) {
          try {
            await releaseSeat(seat.id, eventId, sessionToken)
          } catch {
            // If release fails, still remove from local state —
            // the backend cleanup job will expire it automatically
          }
        }
        setSelectedSeats((prev) => prev.filter((s) => s.seatId !== seat.id))
        if (selectedSeats.length === 1) {
          // Last seat removed — clear session
          setSessionToken(null)
          setReservedUntil(null)
        }
        return
      }

      if (seat.status !== 'AVAILABLE') return

      try {
        const response = await reserveSeats({
          eventId,
          seatIds: [seat.id],
          // sessionToken NOT sent — the backend groups seats by Firebase UID + eventId (H-03)
        })

        // Always use the server-returned token (server is the source of truth)
        setSessionToken(response.sessionToken)
        setReservedUntil(response.reservedUntil)

        // Compute remaining seconds at reservation time (inside event handler — pure)
        if (selectedSeats.length === 0) {
          const expiresAt = new Date(response.reservedUntil).getTime()
          const secs = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
          setInitialDurationSeconds(secs)
        }

        const reserved = response.seats[0]
        if (reserved) {
          setSelectedSeats((prev) => [
            ...prev,
            {
              seatId: reserved.seatId,
              seatLabel: reserved.label,
              reservedUntil: response.reservedUntil,
            },
          ])
        }
      } catch (err) {
        const conflictErr = err as Error & { conflictingSeatIds?: number[] }
        const firstConflict = conflictErr.conflictingSeatIds?.[0]
        if (firstConflict !== undefined) {
          onConflict(firstConflict)
        }
      }
    },
    [selectedSeats, sessionToken],
  )

  const clearSelection = useCallback(() => {
    setSelectedSeats([])
    setSessionToken(null)
    setReservedUntil(null)
    setInitialDurationSeconds(null)
  }, [])

  return { selectedSeats, sessionToken, reservedUntil, initialDurationSeconds, toggleSeat, clearSelection }
}
